import { yupResolver } from '@hookform/resolvers/yup'
import { JSX, useState, useEffect, useMemo } from 'react'
import { FormProvider, useForm, type Resolver } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'
import type { Device } from '@/pages/Device/Device'
import type { Composition } from '../composition'
import { resolveDeviceRole, usePreFlightBase } from '@/hooks/usePreFlightBase'
import SharedPreFlightFields from '@/components/SharedPreFlightFields'
import PreFlightStepBase from '@/pages/calibration/preFlight/PreFlightStepBase'

interface Props {
  device: Device
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  setActiveStep: (step: number) => void
  onNext: () => void
}

type FormValues = {
  country: string
  deviceRoleCode: string
  installedDate: string
  yearsBetweenMaintenance: string
}

const RdiPreFlightStep = ({
  device,
  setComposition,
  setActiveStep,
  onNext,
}: Props): JSX.Element => {
  const { t } = useTranslation('app')
  const {
    composition,
    refreshComposition,
    deviceRoles,
    deviceRolesLoading,
    isSaving,
    effectiveCountry,
    countryItems,
    minInstalledDate,
    deviceRoleRequired,
    submitForm,
    handleCountryChange,
    handleYearsIntervalChange,
  } = usePreFlightBase(device, setComposition, onNext)

  const compositionDeviceRoleCode =
    composition?.composition?.deviceRoleCode ?? ''

  const schema = useMemo(
    () =>
      object({
        country: string().required(),
        deviceRoleCode: deviceRoleRequired
          ? string().required(
              t('device.calibration.preFlightStep.deviceRoleRequired'),
            )
          : string().optional(),
        installedDate: string().optional(),
        yearsBetweenMaintenance: string().optional(),
      }),
    [deviceRoleRequired, t],
  )

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      country: effectiveCountry,
      deviceRoleCode: compositionDeviceRoleCode,
      installedDate: '',
      yearsBetweenMaintenance: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (deviceRoles.length === 0) return
    methods.setValue(
      'deviceRoleCode',
      resolveDeviceRole(deviceRoles, compositionDeviceRoleCode),
      { shouldValidate: true },
    )
  }, [compositionDeviceRoleCode, deviceRoles, methods])

  const { serial, label, type } = device

  return (
    <FormProvider {...methods}>
      <PreFlightStepBase
        serial={serial ?? ''}
        label={label}
        type={type}
        isSaving={isSaving}
        composition={composition}
        onRefreshComposition={refreshComposition}
        onBack={() => setActiveStep(0)}
        onFormSubmit={(e, compositionCheckPasses) => {
          setHasAttemptedSubmit(true)
          void methods.handleSubmit(async (values) => {
            if (!compositionCheckPasses) return
            await submitForm({
              country: values.country,
              deviceRoleCode: values.deviceRoleCode,
            })
          })(e)
        }}
      >
        <SharedPreFlightFields
          control={methods.control}
          isSubmitted={hasAttemptedSubmit}
          hasBoard={false}
          minInstalledDate={minInstalledDate}
          countryItems={countryItems}
          deviceRoles={deviceRoles}
          deviceRolesLoading={deviceRolesLoading}
          hasConfigurableServiceInterval={false}
          isSaving={isSaving}
          onCountryChange={handleCountryChange}
          onYearsIntervalChange={handleYearsIntervalChange}
          device={device}
        />
      </PreFlightStepBase>
    </FormProvider>
  )
}

export default RdiPreFlightStep
